/* eslint-disable no-nested-ternary */
import { Flex, Box, Image } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { TransportEquipmentCountStatus } from "__graphql__/types";
import { config } from "config";
import { routes } from "config/routes";
import { useGetTransportEquipmentCountsQuery } from "flows/Activities/queries/deliveryCheckin/transportEquipments.generated";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import {
  LEKKERLAND_SUPPLIER_ID_PROD,
  LEKKERLAND_SUPPLIER_ID_STAGING,
  REWE_SUPPLIER_ID_PROD,
  REWE_SUPPLIER_ID_STAGING,
} from "flows/Activities/utils/constants";
import DeliveryTruck from "images/random/delivery-truck.svg";
import EquipmentImage from "images/random/equipment.svg";
import ReweLogo from "images/random/rewe-logo.png";
import { useCountryCode } from "shared/hooks/useCountryCode";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Button } from "ui/Button/Button";
import { Spinner } from "ui/Spinner/Spinner";
import { BodyS, TitleM, TitleXS } from "ui/Typography/Typography";

export function PrecountCard() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const countryCode = useCountryCode();

  const isRewe = countryCode === "de";
  const logoImg = isRewe ? ReweLogo : DeliveryTruck;
  const isStaging = config.environment.ENVIRONMENT === "staging";

  const externalSupplierId = isRewe
    ? isStaging
      ? REWE_SUPPLIER_ID_STAGING
      : REWE_SUPPLIER_ID_PROD
    : isStaging
      ? LEKKERLAND_SUPPLIER_ID_STAGING
      : LEKKERLAND_SUPPLIER_ID_PROD;

  const { setDeliveryInfo } = useDeliveryStore(
    useShallow((state) => ({
      setDeliveryInfo: state.setDeliveryInfo,
    })),
  );

  const { data, loading } = useGetTransportEquipmentCountsQuery({
    variables: {
      externalSupplierId: externalSupplierId!,
      status: TransportEquipmentCountStatus.pending,
    },
    skip: !externalSupplierId,
    fetchPolicy: "network-only",
    onError: () => {
      showToastUI({
        title: "components.activities.transport-equipment.count-page.get-counts.error.title",
      });
    },
  });

  if (!externalSupplierId) return null;

  if (loading) return <Spinner alignSelf="center" mb="s200" />;

  const pendingCount = data?.getTransportEquipmentCounts.transportEquipmentCounts[0] ?? null;

  const goToPrecountEquipmentFlow = () => {
    setDeliveryInfo({
      externalSupplierId,
    });
    navigate(routes.activities.transportEquipment.precount);
  };

  if (!pendingCount) {
    return (
      <Flex bgColor="white" px="s200" py="s300" gap="s300" borderRadius="sm">
        <Image src={EquipmentImage} boxSize="48px" borderRadius="full" />
        <Box>
          <BodyS mb="s100">
            {intl.formatMessage({ id: "pages.activities.deliveries.prepare-equipment.body" })}
          </BodyS>
          <Button
            variant="secondary"
            onClick={goToPrecountEquipmentFlow}
            data-testid="delivery-checkin-cta-button"
          >
            {intl.formatMessage({ id: "pages.activities.deliveries.prepare-equipment.cta" })}
          </Button>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      bgColor="white"
      px="s200"
      py="s300"
      gap="s300"
      borderRadius="sm"
      align="center"
      onClick={goToPrecountEquipmentFlow}
    >
      <Image src={logoImg} borderRadius="sm" />
      <Flex direction="column" gap="s50">
        <TitleM>
          {[REWE_SUPPLIER_ID_PROD, REWE_SUPPLIER_ID_STAGING].includes(
            pendingCount.externalSupplierId,
          )
            ? "Rewe Markt GmBH"
            : [LEKKERLAND_SUPPLIER_ID_PROD, LEKKERLAND_SUPPLIER_ID_STAGING].includes(
                  pendingCount.externalSupplierId,
                )
              ? "Lekkerland"
              : "Other Supplier"}
        </TitleM>
        <TitleXS>{dayjs(pendingCount.createdAt).format("DD.MM.YYYY")}</TitleXS>
        {pendingCount.lineItems.map((item) => (
          <BodyS key={item.equipmentTypeId}>{`${item.name}: ${item.returned}`}</BodyS>
        ))}
      </Flex>
    </Flex>
  );
}
